import React, { useEffect, useState, useCallback } from 'react';
import { Box, Menu, MenuItem, Modal, Typography } from '@mui/material';
import dayjs from 'dayjs';
import AnalyticsHeader from './CommonComponents/AnalyticsHeader';
import SafetyAnalyticsScreen from './SafetyAnalytics/SafetyAnalyticsScreen';
import AnalyticsCompilanceScreen from './Analytics_Compliance/AnalyticsCompilanceScreen';
import ProductivityAnalytics from './ProductivityAnalytics/ProductivityAnalytics';
import DashboardAnalyticsFilterData from './CommonComponents/DashboardAnalyticsFilterData';
import AnalyticsManageSectionLayout, {
  AnalyticsSectionsConfig,
} from './CommonComponents/AnalyticsManageSectionLayout';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import apiClient from 'packages/novous_web/src/api/apiClient';

import FleetDistanceIcon from 'packages/novous_web/src/assets/icons/Distance.svg';
import MedianSpeedIcon from 'packages/novous_web/src/assets/icons/Meter.svg';
import OverspeedingIcon from 'packages/novous_web/src/assets/icons/Meter.svg';
import ForwardCollisionIcon from 'packages/novous_web/src/assets/icons/ForwardCollisionGray.svg';
import DistractionIcon from 'packages/novous_web/src/assets/icons/DistractedGray.svg';
import HarshDrivingIcon from 'packages/novous_web/src/assets/icons/HarshAccelerationGray.svg';
import DrowsinessIcon from 'packages/novous_web/src/assets/icons/DrowsinessGray.svg';
import ManageViewsIcon from 'packages/novous_web/src/assets/icons/Setting_Fill.svg';
import DownloadIcon from 'packages/novous_web/src/assets/icons/download.svg';
import LaneDepartureIcon from 'packages/novous_web/src/assets/icons/lanedeparturewarning.svg';
import seatBeltIcon from 'packages/novous_web/src/assets/icons/SeatbeltGray.svg';
import SmokingIcon from 'packages/novous_web/src/assets/icons/SmokingGray.svg';
import PhoneCallingIcon from 'packages/novous_web/src/assets/icons/PhoneCallingGray.svg';
import ManageViewModal from './CommonComponents/ManageViewModal';
import UpdateViewIcon from 'packages/novous_web/src/assets/icons/Save.svg';
import DeleteIcon from 'packages/novous_web/src/assets/icons/Delete.svg';
import DrivingIcon from 'packages/novous_web/src/assets/icons/DriveGray.svg';
import ContinuousDrivingIcon from 'packages/novous_web/src/assets/icons/tabler_clock-filled.svg';
import EcoIcon from 'packages/novous_web/src/assets/icons/Eco.svg';

const ANALYTICS_CONFIG = {
  safety: [
    { id: 1024, title: 'Fleet Distance Travelled', icon: FleetDistanceIcon },
    { id: 1003, title: 'Phone Calling Alerts', icon: PhoneCallingIcon },
    { id: 1006, title: 'Overspeeding Events', icon: OverspeedingIcon },
    { id: 1007, title: 'Forward Collision Alerts', icon: ForwardCollisionIcon },
    { id: 1013, title: 'Distraction Alerts', icon: DistractionIcon },
    { id: 1028, title: 'Harsh Driving', icon: HarshDrivingIcon },
    { id: 1202, title: 'Seat Belt Alerts', icon: seatBeltIcon },
    { id: 1067, title: 'Drowsiness Alerts', icon: DrowsinessIcon },
    { id: 999, title: 'Warnings by Category', icon: HarshDrivingIcon },
    { id: 1004, title: 'Safety Score', icon: HarshDrivingIcon },
  ],
  compliance: [
    { id: 1102, title: 'Fleet Distance Travelled', icon: FleetDistanceIcon },
    { id: 1103, title: 'Driver Distraction Alerts', icon: DistractionIcon },
    { id: 1035, title: 'Lane Departure Alerts', icon: LaneDepartureIcon },
    { id: 1034, title: 'Seat Belt Violations', icon: seatBeltIcon },
    { id: 1041, title: 'Smoking Violations', icon: SmokingIcon },
    { id: 1042, title: 'Phone Calling Violations', icon: PhoneCallingIcon },
    { id: 1044, title: 'Warning count', icon: HarshDrivingIcon },
    { id: 1032, title: 'Overall Compliance Score', icon: HarshDrivingIcon },
    { id: 1038, title: 'Frequent Violators', icon: HarshDrivingIcon },
    { id: 1033, title: 'Compliance Density Heatmap', icon: HarshDrivingIcon },
    { id: 1046, title: 'Lane Departure with Gps Speed', icon: PhoneCallingIcon },
    // { id: 1039, title: 'Numerical Analysis of Recent Week', icon: PhoneCallingIcon },
    { id: 1302, title: 'Warning per 100 km', icon: PhoneCallingIcon },
    { id: 1040, title: 'Compliant vehicles label', icon: PhoneCallingIcon },
    { id: 1110, title: 'Seat Belt worn percentage', icon: PhoneCallingIcon },
    { id: 1031, title: 'Weekly Compliance Score', icon: PhoneCallingIcon },
    { id: 1111, title: 'India Heat Map', icon: HarshDrivingIcon },
  ],
  productivity: [
    { id: 1100, title: 'Fleet Distance Travelled', icon: FleetDistanceIcon },
    { id: 1101, title: 'Average Median Speed', icon: OverspeedingIcon },
    { id: 1058, title: 'Driving Time', icon: DrivingIcon },
    { id: 1068, title: 'Total Field Time', icon: ContinuousDrivingIcon },
    { id: 1073, title: 'Low Speeding Hours', icon: OverspeedingIcon },
    { id: 1072, title: 'Eco Driving Hours', icon: EcoIcon },
    { id: 1071, title: 'Driver Productivity Index', icon: FleetDistanceIcon },
    { id: 1203, title: 'Most Utilised Vehicles', icon: FleetDistanceIcon },
    { id: 1300, title: 'Idle Time by Vehicle', icon: FleetDistanceIcon },
    { id: 1303, title: ' Distance Contribution by Vehicle & Date (in KM)', icon: FleetDistanceIcon },
    { id: 1057, title: 'Day and Night Driving Hours', icon: FleetDistanceIcon },
    { id: 1075, title: 'Eco-Driving and Low-Speed Comparision', icon: FleetDistanceIcon },
    { id: 1065, title: 'Productivity and Downtime', icon: FleetDistanceIcon },
    { id: 1055, title: 'Daily vehicle usage (Day vs Night)', icon: FleetDistanceIcon },
    { id: 1074, title: 'Driving Hours Breakdown', icon: FleetDistanceIcon },
    { id: 1304, title: 'Daily Eco-Driving Hours per Vehicle', icon: FleetDistanceIcon },
    { id: 1070, title: 'Daily time breakdown by vehicle', icon: FleetDistanceIcon },
  ],
};

interface FilterState {
  vehicleIds: string[];
  vehicleGroup: string;
  dateRange: string;
  startDate?: string;
  endDate?: string;
  view: string;
}

const DashboardAnalyticScreen: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedView, setSelectedView] = useState(() => {
    return sessionStorage.getItem('analyticsSelectedView') || 'Safety (Default view)';
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    vehicleIds: [],
    vehicleGroup: '',
    dateRange: '7 Days',
    view: 'Safety (Default view)',
  });
  // Store chart-specific date ranges for Edit Data modal overrides
  const [chartDateRanges, setChartDateRanges] = useState<{
    [chartId: number]: { startDate: string; endDate: string };
  }>({});
  const [vehicleOptions, setVehicleOptions] = useState<{ value: string; label: string }[]>([]);
  const [groupOptions, setGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [apiData, setApiData] = useState<{ [key: string]: any[] }>({});
  const [chartData1001, setChartData1001] = useState<any[]>([]);
  const [chartData1005, setChartData1005] = useState<any[]>([]);
  const [paginatedData, setPaginatedData] = useState<any>(null);
  const [paginatedData1039, setPaginatedData1039] = useState<any>(null);
  const [paginatedData1603, setPaginatedData1603] = useState<any>(null);
  const [paginatedData1110, setPaginatedData1110] = useState<any>(null);
  const [paginatedData1031, setPaginatedData1031] = useState<any>(null);
  const [paginatedData1600, setPaginatedData1600] = useState<any>(null);
  const [paginatedData1105, setPaginatedData1105] = useState<any>(null);
  const [paginatedData1203, setPaginatedData1203] = useState<any>(null);
  const [paginatedData1604, setPaginatedData1604] = useState<any>(null);
  const [paginatedData1608, setPaginatedData1608] = useState<any>(null);
  const [safetyDashboardTableData, setSafetyDashboardTableData] = useState<any[]>([]);
  const [timeBucketTableData, setTimeBucketTableData] = useState<any[]>([]);
  const [heatmapTableData, setHeatmapTableData] = useState<any[]>([]);
  const [safetyTablePaginationInfo, setSafetyTablePaginationInfo] = useState<any>({
    lastEvaluatedKey: null,
    hasMorePages: false,
    totalRecords: 0,
  });
  const [timeBucketTablePaginationInfo, setTimeBucketTablePaginationInfo] = useState<any>({
    lastEvaluatedKey: null,
    hasMorePages: false,
    totalRecords: 0,
  });
  const [heatmapTablePaginationInfo, setHeatmapTablePaginationInfo] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    hasMorePages: false,
  });
  const [allHeatmapTableData, setAllHeatmapTableData] = useState<any[]>([]);
  const [selectedWarningType, setSelectedWarningType] = useState<string | null>(null);
  const [safetyAnalyticsCardData, setSafetyAnalyticsCardData] = useState<{ [key: number]: any }>({});

  // Pagination info for each safety analytics card table
  const [safetyAnalyticsCardPaginationInfo, setSafetyAnalyticsCardPaginationInfo] = useState<{
    [key: number]: {
      lastEvaluatedKey?: any;
      hasMorePages?: boolean;
      totalRecords?: number;
    };
  }>({});

  // Compliance Analytics Card Data and Pagination
  const [complianceAnalyticsCardData, setComplianceAnalyticsCardData] = useState<{ [key: number]: any }>({});
  const [complianceAnalyticsCardPaginationInfo, setComplianceAnalyticsCardPaginationInfo] = useState<{
    [key: number]: {
      lastEvaluatedKey?: any;
      hasMorePages?: boolean;
      totalRecords?: number;
    };
  }>({});

  const [productivityAnalyticsCardData, setProductivityAnalyticsCardData] = useState<{ [key: number]: any }>(
    {},
  );
  const [productivityAnalyticsCardPaginationInfo, setProductivityAnalyticsCardPaginationInfo] = useState<{
    [key: number]: {
      lastEvaluatedKey?: any;
      hasMorePages?: boolean;
      totalRecords?: number;
    };
  }>({});

  const [manageAnchorEl, setManageAnchorEl] = useState<null | HTMLElement>(null);
  const [isManageViewsModalOpen, setIsManageViewsModalOpen] = useState(false);
  const [isManageSectionsModalOpen, setIsManageSectionsModalOpen] = useState(false);
  const [chartData1033, setChartData1033] = useState<any[]>([]);
  const [chartData1111, setChartData1111] = useState<any[]>([]);

  // Sections configuration state for all views
  const [sectionsConfig, setSectionsConfig] = useState<AnalyticsSectionsConfig>({
    // Safety sections
    'analytics-cards': true,
    'warning-by-category': true,
    'warning-by-time-bucket': true,
    'safety-score': true,
    'hourly-distribution': true,
    'numerical-analysis': true,
    // Compliance sections - individual cards
    'compliance-analytics-cards': true,
    'compliance-warning-count': true,
    'compliance-frequent-violators': true,
    'compliance-overall-score': true,
    'compliance-density-map': true,
    'compliance-lane-departure': true,
    'compliance-numerical-analysis': true,
    'compliance-warning-per-km': true,
    'compliance-compliant-vehicles': true,
    'compliance-seat-belt-warnings': true,
    'compliance-weekly-score': true,
    'compliance-india-heat-map': true,
    // Productivity sections
    'productivity-analytics-cards': true,
    'productivity-charts': true,
  });

  const open = Boolean(anchorEl);
  const openManageMenu = Boolean(manageAnchorEl);

  const handleManageSectionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setManageAnchorEl(event.currentTarget);
  };

  const handleManageMenuClose = () => {
    setManageAnchorEl(null);
  };

  const handleManageViewsClick = () => {
    handleManageMenuClose();
    setIsManageViewsModalOpen(true);
  };

  const handleManageSectionsClick2 = () => {
    handleManageMenuClose();
    setIsManageSectionsModalOpen(true);
  };

  const handleDownloadClick = () => {
    handleManageMenuClose();
    // You can trigger your download logic here
  };

  const handleCloseManageViewsModal = () => {
    setIsManageViewsModalOpen(false);
  };

  const handleCloseManageSectionsModal = () => {
    setIsManageSectionsModalOpen(false);
  };

  const handleToggleSection = (sectionId: keyof AnalyticsSectionsConfig) => {
    setSectionsConfig((prevConfig) => ({
      ...prevConfig,
      [sectionId]: !prevConfig[sectionId],
    }));
  };

  const isRoleAdmin = user?.authorities?.includes('ROLE_ADMIN');
  console.log('User Role Admin Status:', isRoleAdmin);

  // Helper to resolve the fleet ID for API calls
  // For ROLE_ADMIN: use the selected fleet manager ID from Account Settings (sessionStorage)
  // For others: use the customer_id (default fleet)
  const getFleetIdForApi = () => {
    if (isRoleAdmin) {
      // If AccountSettings cleared only analytics, return null for analytics APIs
      const analyticsCleared = sessionStorage.getItem('analyticsFleetCleared') === 'true';
      if (analyticsCleared) {
        sessionStorage.removeItem('analyticsFleetCleared');
        sessionStorage.removeItem('selectedFleetManager');
        return null;
      }

      // Use selected fleet manager ID from sessionStorage (set in AccountSettings)
      // Returns null if nothing is selected yet (cleared)
      return sessionStorage.getItem('selectedFleetManager');
    }
    return user?.customer_id;
  };

  // Generic function to fetch chart data for different chart IDs
  const fetchChartData = useCallback(
    async (
      chartId: number,
      setStateFunction: React.Dispatch<React.SetStateAction<any[]>>,
      overrideStartDate?: string,
      overrideEndDate?: string,
    ) => {
      try {
        // Use override dates if provided, otherwise use filters or defaults
        let startDate =
          overrideStartDate || filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        let endDate = overrideEndDate || filters.endDate || dayjs().format('YYYY-MM-DD');

        const fleetId = getFleetIdForApi();

        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        // Build URL with conditional parameters
        let url = `${baseurl}/api/v1/analytics_dashboard/api/chart_data/${chartId}?start_date=${startDate}&end_date=${endDate}`;

        // If vehicle IDs are selected, use them; otherwise use fleet_manager_id
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          const vehicleIds = filters.vehicleIds.join(',');
          url += `&vehicle_ids=${vehicleIds}`;
        } else {
          url += `&fleet_manager_id=${getFleetIdForApi()}`;
        }

        // If group IDs are selected, use them; otherwise use fleet_manager_id
        if (filters.vehicleGroup) {
          const vehicleGroup = filters.vehicleGroup;
          url += `&vehicleGroup=${vehicleGroup}`;
        } else {
          url += `&fleet_manager_id=${getFleetIdForApi()}`;
        }

        // Add fleetAdminId parameter - use user.id for ROLE_ADMIN, user.parentId for others
        const fleetAdminId = isRoleAdmin ? user.id : user.parentId;
        url += `&fleet_admin_id=${fleetAdminId}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();

        // Extract the data array from the response
        const dataArray = responseData.data || [];
        setStateFunction(dataArray);
      } catch (error) {
        setStateFunction([]);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          table_id: 1009,
          start_date: startDate,
          end_date: endDate,
          limit: 4,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // If group IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        const fleetId = getFleetIdForApi();

        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPaginatedData(data);
      } catch (error) {
        setPaginatedData(null);
      }
    },
    [user.customer_id, filters],
  );

  const handlePaginationChange = (cursor: any) => {
    fetchPaginatedData(cursor);
  };

  const fetchPaginatedData1039 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          table_id: 1039,
          start_date: startDate,
          end_date: endDate,
          limit: 4,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        // if (filters.vehicleIds && filters.vehicleIds.length > 0) {
        //   payload.vehicle_ids = filters.vehicleIds;
        // } else {
        payload.fleetId = getFleetIdForApi();
        // }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1039 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1039 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1039(data);
      } catch (error) {
        console.error('Error fetching chart 1039 data:', error);
        setPaginatedData1039(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1603 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1603,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1603 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1603 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1603(data);
      } catch (error) {
        console.error('Error fetching chart 1603 data:', error);
        setPaginatedData1603(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1110 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1110,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1110 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1110 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1110(data);
      } catch (error) {
        console.error('Error fetching chart 1110 data:', error);
        setPaginatedData1110(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1031 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1031,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1031 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1031 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1031(data);
      } catch (error) {
        console.error('Error fetching chart 1031 data:', error);
        setPaginatedData1031(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1600 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1600,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1600 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1600 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1600(data);
      } catch (error) {
        console.error('Error fetching chart 1600 data:', error);
        setPaginatedData1600(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1105 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1105,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1105 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1105 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1105(data);
      } catch (error) {
        console.error('Error fetching chart 1105 data:', error);
        setPaginatedData1105(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1203 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1203,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1203 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1203 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1203(data);
      } catch (error) {
        console.error('Error fetching chart 1203 data:', error);
        setPaginatedData1203(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1604 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1604,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1604 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1604 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1604(data);
      } catch (error) {
        console.error('Error fetching chart 1604 data:', error);
        setPaginatedData1604(null);
      }
    },
    [user.customer_id, filters],
  );

  const fetchPaginatedData1608 = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1608,
          start_date: startDate,
          end_date: endDate,
          // limit: 10,
        };

        // Use selected vehicles if present, otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Use selected groups if present, otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        console.log('=== Fetching Chart 1608 Pagination Data ===');
        console.log('Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(
          `${baseurl}/api/v1/analytics_dashboard/api/paginate_by_chart_id
`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('=== Chart 1608 Response Data ===');
        console.log('Data:', data);
        setPaginatedData1608(data);
      } catch (error) {
        console.error('Error fetching chart 1608 data:', error);
        setPaginatedData1608(null);
      }
    },
    [user.customer_id, filters],
  );

  const handlePaginationChange1039 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1039 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1039(cursor);
  };

  const handlePaginationChange1603 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1603 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1603(cursor);
  };

  const handlePaginationChange1110 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1110 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1110(cursor);
  };

  const handlePaginationChange1031 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1031 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1031(cursor);
  };

  const handlePaginationChange1600 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1600 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1600(cursor);
  };

  const handlePaginationChange1105 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1105 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1105(cursor);
  };

  const handlePaginationChange1203 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1203 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1203(cursor);
  };

  const handlePaginationChange1604 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1604 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1604(cursor);
  };

  const handlePaginationChange1608 = (cursor: any) => {
    console.log('=== Pagination Change for Chart 1608 ===');
    console.log('Cursor:', cursor);
    fetchPaginatedData1608(cursor);
  };

  // Fetch Safety Analytics Card Data with table IDs
  const fetchSafetyAnalyticsCardData = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        // Table IDs for safety analytics cards
        const tableIds = [1308, 1309, 1310, 1311, 1312, 1313, 1315, 1316];

        // Create payloads for each table ID
        const allTableData: { [key: number]: any } = {};

        for (const tableId of tableIds) {
          const payload: any = {
            table_id: tableId,
            start_date: startDate,
            end_date: endDate,
            limit: 10,
          };

          // If vehicle IDs are selected, use them; otherwise use fleetId
          if (filters.vehicleIds && filters.vehicleIds.length > 0) {
            payload.vehicle_ids = filters.vehicleIds;
          } else {
            payload.fleetId = getFleetIdForApi();
          }

          // If group IDs are selected, use them; otherwise use fleetId
          if (filters.vehicleGroup) {
            payload.vehicleGroup = filters.vehicleGroup;
          } else {
            payload.fleetId = getFleetIdForApi();
          }

          // Add fleetAdminId
          if (isRoleAdmin) payload.fleetAdminId = user.id;

          // Only add next_cursor if it's provided (for next page)
          if (cursor) {
            payload.next_cursor = cursor;
          } else {
            payload.next_cursor = null;
          }

          const fleetId = getFleetIdForApi();
          const baseurl =
            fleetId == '685e1eec79b388464c5cd08e'
              ? 'https://backend.novusomnifleet.com'
              : 'https://novusai.novusomnifleet.com';

          const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          allTableData[tableId] = data;
        }

        setSafetyAnalyticsCardData(allTableData);
        return allTableData;
      } catch (error) {
        return null;
      }
    },
    [user.customer_id, filters],
  );

  // Fetch data for a single table ID on demand (lazy loading when user clicks table icon)
  const fetchSingleTableData = useCallback(
    async (tableId: number, cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          table_id: tableId,
          start_date: startDate,
          end_date: endDate,
          limit: 10,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // If group IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Add cursor if provided (for pagination)
        payload.next_cursor = cursor || null;

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Update the state with the fetched data
        // Always replace data - store only the current page's data
        setSafetyAnalyticsCardData((prev) => ({
          ...prev,
          [tableId]: data,
        }));

        // Update pagination info for this table
        setSafetyAnalyticsCardPaginationInfo((prev) => ({
          ...prev,
          [tableId]: {
            lastEvaluatedKey: data.next_cursor || null,
            hasMorePages: !!data.next_cursor,
            totalRecords: prev[tableId]?.totalRecords || data.data?.length || 0,
          },
        }));

        return data;
      } catch (error) {
        return null;
      }
    },
    [user.customer_id, filters],
  );

  // Handle pagination for safety analytics card tables
  const handleSafetyAnalyticsCardTablePagination = useCallback(
    (tableId: number, cursor?: any) => {
      fetchSingleTableData(tableId, cursor);
    },
    [fetchSingleTableData],
  );

  // Fetch Compliance Analytics Card Data with table IDs
  const fetchComplianceAnalyticsCardData = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        // Table IDs for compliance analytics cards
        const tableIds = [1308, 1320, 1321, 1315, 1322, 1309, 1331, 1332, 1335, 1334, 1333];

        // Create payloads for each table ID
        const allTableData: { [key: number]: any } = {};

        for (const tableId of tableIds) {
          const payload: any = {
            table_id: tableId,
            start_date: startDate,
            end_date: endDate,
            limit: 10,
          };

          // If vehicle IDs are selected, use them; otherwise use fleetId
          if (filters.vehicleIds && filters.vehicleIds.length > 0) {
            payload.vehicle_ids = filters.vehicleIds;
          } else {
            payload.fleetId = getFleetIdForApi();
          }

          // If group IDs are selected, use them; otherwise use fleetId
          if (filters.vehicleGroup) {
            payload.vehicleGroup = filters.vehicleGroup;
          } else {
            payload.fleetId = getFleetIdForApi();
          }

          // Add fleetAdminId
          if (isRoleAdmin) payload.fleetAdminId = user.id;

          // Only add next_cursor if it's provided (for next page)
          if (cursor) {
            payload.next_cursor = cursor;
          } else {
            payload.next_cursor = null;
          }

          const fleetId = user.customer_id;
          const baseurl =
            fleetId == '685e1eec79b388464c5cd08e'
              ? 'https://backend.novusomnifleet.com'
              : 'https://novusai.novusomnifleet.com';

          const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          allTableData[tableId] = data;
        }

        setComplianceAnalyticsCardData(allTableData);
        return allTableData;
      } catch (error) {
        return null;
      }
    },
    [user.customer_id, filters],
  );

  // Fetch data for a single compliance table ID on demand (lazy loading when user clicks table icon)
  const fetchSingleComplianceTableData = useCallback(
    async (tableId: number, cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          table_id: tableId,
          start_date: startDate,
          end_date: endDate,
          limit: 10,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // If group IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Add cursor if provided (for pagination)
        payload.next_cursor = cursor || null;

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Update the state with the fetched data
        setComplianceAnalyticsCardData((prev) => ({
          ...prev,
          [tableId]: data,
        }));

        // Update pagination info for this table
        setComplianceAnalyticsCardPaginationInfo((prev) => ({
          ...prev,
          [tableId]: {
            lastEvaluatedKey: data.next_cursor || null,
            hasMorePages: !!data.next_cursor,
            totalRecords: prev[tableId]?.totalRecords || data.data?.length || 0,
          },
        }));

        return data;
      } catch (error) {
        return null;
      }
    },
    [user.customer_id, filters],
  );

  // Handle pagination for compliance analytics card tables
  const handleComplianceAnalyticsCardTablePagination = useCallback(
    (tableId: number, cursor?: any) => {
      fetchSingleComplianceTableData(tableId, cursor);
    },
    [fetchSingleComplianceTableData],
  );

  // Fetch data for a single productivity table ID on demand (lazy loading when user clicks table icon)
  const fetchSingleProductivityTableData = useCallback(
    async (tableId: number, cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          table_id: tableId,
          start_date: startDate,
          end_date: endDate,
          limit: 10,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // If group IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Add cursor if provided (for pagination)
        payload.next_cursor = cursor || null;

        console.log('[Productivity Table] Fetching tableId:', tableId, 'Payload:', payload);

        const fleetId = getFleetIdForApi();
        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('[Productivity Table] Response for tableId:', tableId, 'Data:', data);

        // Update the state with the fetched data - store by tableId
        setProductivityAnalyticsCardData((prev) => ({
          ...prev,
          [tableId]: data,
        }));

        // Update pagination info for this table
        setProductivityAnalyticsCardPaginationInfo((prev) => ({
          ...prev,
          [tableId]: {
            lastEvaluatedKey: data.next_cursor || null,
            hasMorePages: !!data.next_cursor,
            totalRecords: prev[tableId]?.totalRecords || data.data?.length || 0,
          },
        }));

        return data;
      } catch (error) {
        console.error('[Productivity Table] Error fetching data:', error);
        return null;
      }
    },
    [user.customer_id, filters],
  );

  // Handle pagination for productivity analytics card tables
  const handleProductivityAnalyticsCardTablePagination = useCallback(
    (tableId: number, cursor?: any) => {
      console.log(
        '[Pagination] handleProductivityAnalyticsCardTablePagination called with tableId:',
        tableId,
        'cursor:',
        cursor,
      );
      fetchSingleProductivityTableData(tableId, cursor);
    },
    [fetchSingleProductivityTableData],
  );

  // Fetch Safety Dashboard Analytics data
  const fetchSafetyDashboardAnalytics = useCallback(
    async (cursor?: any, warningType?: string | null) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        // Use the passed warningType if provided, otherwise use the stored one
        const typeToUse = warningType !== undefined ? warningType : selectedWarningType;

        // Update selected warning type if a new one is provided
        if (warningType !== undefined) {
          setSelectedWarningType(warningType);
        }

        const payload: any = {
          startDate: startDate,
          endDate: endDate,
          // If warningType is provided and cursor is not, reset to first page (no cursor)
          // If cursor is provided, use it; otherwise null for first page
          lastEvaluatedKey: cursor || null,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // If group IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Only add warningType if it's provided and not null (i.e., not "Select All")
        if (typeToUse) {
          payload.warningType = typeToUse;
          // Reset pagination when changing warning type filter
          if (!cursor) {
            payload.lastEvaluatedKey = null;
          }
        }

        const response = await apiClient({
          url: API_ENDPOINTS.dashboard.SAFETY_DASHBOARD_ANALYTICS,
          method: 'POST',
          data: payload,
        });

        // Extract pagination info from response
        // Handle DynamoDB format lastEvaluatedKey
        let lastEvaluatedKey = null;
        const rawLastEvaluatedKey = response.data?.data?.lastEvaluatedKey;
        if (rawLastEvaluatedKey) {
          // Convert from DynamoDB format to plain object
          lastEvaluatedKey = {
            device_id: rawLastEvaluatedKey.device_id?.s || null,
            fleet_id: rawLastEvaluatedKey.fleet_id?.s || null,
            warningSortKey: rawLastEvaluatedKey.warningSortKey?.s || null,
          };
        }

        // hasMorePages is true if lastEvaluatedKey exists (there are more pages)
        const hasMorePages = lastEvaluatedKey !== null;
        const totalRecords = response.data?.data?.totalRecords || 0;

        // Transform API response data to table format
        if (response.data && response.data.data && response.data.data.actualWarningList) {
          // Transform for Warning by Category table
          const categoryTableData = response.data.data.actualWarningList.map((item: any) => ({
            timestamp: item.date_time
              ? item.date_time
                  .replace(/_/g, '-')
                  .replace(/(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})/, '$1-$2-$3 $4:$5:$6')
              : '',
            warningType: item.warningString || '',
            driverName: item.driverName || 'NA',
            vehicleNumber: item.vehicleId || '',
            group: item.vehicleGroup || '',
            speedAtEvent: item.gpsSpeed || 0,
            gpsLocation: `${item.gpsLat}, ${item.gpsLong}`,
          }));
          setSafetyDashboardTableData(categoryTableData);

          // Update pagination info for category table
          setSafetyTablePaginationInfo({
            lastEvaluatedKey,
            hasMorePages,
            totalRecords,
          });

          // Transform for Warning by Time Bucket table
          const timeBucketData = response.data.data.actualWarningList.map((item: any) => {
            // Determine time bucket based on hour
            const dateTimeStr = item.date_time; // Format: "2025_12_02_19_41_58"
            let hour = 0;
            if (dateTimeStr) {
              const parts = dateTimeStr.split('_');
              if (parts.length >= 4) {
                hour = parseInt(parts[3], 10);
              }
            }

            let timeBucket = '';
            if (hour >= 0 && hour < 6) timeBucket = '00-06';
            else if (hour >= 6 && hour < 12) timeBucket = '06-12';
            else if (hour >= 12 && hour < 18) timeBucket = '12-18';
            else if (hour >= 18 && hour < 24) timeBucket = '18-24';

            return {
              timestamp: item.date_time
                ? item.date_time
                    .replace(/_/g, '-')
                    .replace(/(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})/, '$1-$2-$3 $4:$5:$6')
                : '',
              driverName: item.driverName || 'NA',
              vehicleId: item.vehicleId || '',
              alertType: item.warningString || '',
              timeBucket: timeBucket,
              speed: item.gpsSpeed !== undefined && item.gpsSpeed !== null ? item.gpsSpeed : 0,
              location: `${item.gpsLat}, ${item.gpsLong}`,
              tripId: item.tripId || '',
              distanceCovered: item.km !== undefined && item.km !== null ? item.km : 0,
            };
          });
          setTimeBucketTableData(timeBucketData);

          // Update pagination info for time bucket table
          setTimeBucketTablePaginationInfo({
            lastEvaluatedKey,
            hasMorePages,
            totalRecords,
          });
        }

        return response.data;
      } catch (error) {
        return null;
      }
    },
    [user.customer_id, filters.startDate, filters.endDate, selectedWarningType],
  );

  // Function to transform heatmap data to table format
  const transformHeatmapDataToTable = useCallback((heatmapData: any[]) => {
    return heatmapData.map((item: any) => ({
      dayBucket: item.day, // e.g., "Mon 01"
      hourBucket: item.hour, // e.g., "0-3"
      eventCount: item.value, // numeric value
    }));
  }, []);

  // Function to update heatmap table data when table button is clicked
  const updateHeatmapTableData = useCallback((tableData: any[]) => {
    // Store all the data and set the first page
    setAllHeatmapTableData(tableData);

    // Calculate pagination info
    const pageSize = 10;
    const totalRecords = tableData.length;
    const hasMorePages = totalRecords > pageSize;

    // Set first page data
    setHeatmapTableData(tableData.slice(0, pageSize));
    setHeatmapTablePaginationInfo({
      currentPage: 1,
      pageSize: pageSize,
      totalRecords: totalRecords,
      hasMorePages: hasMorePages,
    });
  }, []);

  // Function to handle heatmap table pagination
  const handleHeatmapTablePaginationChange = useCallback(
    (newPage: number) => {
      const pageSize = 10;
      const startIndex = (newPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      const paginatedData = allHeatmapTableData.slice(startIndex, endIndex);
      setHeatmapTableData(paginatedData);

      const totalRecords = allHeatmapTableData.length;
      const hasMorePages = endIndex < totalRecords;

      setHeatmapTablePaginationInfo({
        currentPage: newPage,
        pageSize: pageSize,
        totalRecords: totalRecords,
        hasMorePages: hasMorePages,
      });
    },
    [allHeatmapTableData],
  );

  // Function to handle heatmap table icon click and transform data
  const handleHeatmapTableIconClick = useCallback((selectedAlert: string | null) => {
    // The table data will be set by SafetyAnalyticsScreen through updateHeatmapTableData callback
    // This callback is just a trigger for the child component to know when to transform and send data
  }, []);

  const fetchVehicleOptions = async () => {
    try {
      // Using the same API endpoint as other filters (My Expenses, FilterDataModal, etc.)
      if (user?.customer_id) {
        const response = await apiClient({
          url: API_ENDPOINTS.dashboard.VEHICLE,
          method: 'POST',
          data: { fleetId: user.customer_id },
        });

        const data = response.data;

        const options = data.map((vehicle: any) => ({
          value: vehicle.vehicleRegistrationNo,
          label: vehicle.vehicleRegistrationNo,
        }));
        setVehicleOptions(options);
      }
    } catch (error) {
      setVehicleOptions([]);
    }
  };

  const fetchGroupOptions = async () => {
    try {
      // Using the same API endpoint as other filters (My Expenses, FilterDataModal, etc.)
      if (user?.customer_id) {
        const response = await apiClient({
          url: `${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id}`,
          method: 'GET',
        });

        console.log('fetchGroupOptions response:', response);

        // API returns { data: [...], message: "...", status: 200 }
        const groupsArray = response.data?.data || response.data;

        console.log('groupsArray:', groupsArray);

        const options = Array.isArray(groupsArray)
          ? groupsArray.map((group: string) => ({
              value: group,
              label: group,
            }))
          : [];

        console.log('transformed options:', options);
        setGroupOptions(options);
      }
    } catch (error) {
      console.error('Error fetching group options:', error);
      setGroupOptions([]);
    }
  };

  // Fetch vehicle options on component mount
  useEffect(() => {
    if (user?.customer_id) {
      fetchVehicleOptions();
      fetchGroupOptions();
    }
  }, [user?.customer_id]);

  // Fetch API data when filters or view changes
  useEffect(() => {
    fetchAllAnalyticsData();
  }, [
    filters.startDate,
    filters.endDate,
    filters.vehicleIds,
    filters.vehicleGroup,
    selectedView,
    user.customer_id,
  ]);

  // Fetch both chart_data/1001 and chart_data/1005 APIs (Safety view only)
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Safety')) {
      fetchChartData(1001, setChartData1001);
      fetchChartData(1005, setChartData1005);
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchChartData, selectedView]);

  // Fetch paginated data for Safety view only
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Safety')) {
      fetchPaginatedData();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData, selectedView]);

  // Fetch paginated data for chart 1039 (Numerical Analysis of Recent Week)
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      console.log('=== Compliance View Detected - Fetching Chart 1039 ===');
      fetchPaginatedData1039();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1039, selectedView]);

  // Fetch paginated data for chart 1603 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      console.log('=== Compliance View Detected - Fetching Chart 1603 ===');
      fetchPaginatedData1603();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1603, selectedView]);

  // Fetch paginated data for chart 1110 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      console.log('=== Compliance View Detected - Fetching Chart 1110 ===');
      fetchPaginatedData1110();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1110, selectedView]);

  // Fetch paginated data for chart 1031 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      console.log('=== Compliance View Detected - Fetching Chart 1031 ===');
      fetchPaginatedData1031();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1031, selectedView]);

  // Fetch paginated data for chart 1600 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      console.log('=== Compliance View Detected - Fetching Chart 1600 ===');
      fetchPaginatedData1600();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1600, selectedView]);

  // Fetch paginated data for chart 1105 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Productivity')) {
      console.log('=== Productivity View Detected - Fetching Chart 1105 ===');
      fetchPaginatedData1105();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1105, selectedView]);

  // Fetch paginated data for chart 1203 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Productivity')) {
      console.log('=== Productivity View Detected - Fetching Chart 1203 ===');
      fetchPaginatedData1203();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1203, selectedView]);

  // Fetch paginated data for chart 1604 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Productivity')) {
      console.log('=== Productivity View Detected - Fetching Chart 1604 ===');
      fetchPaginatedData1604();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1604, selectedView]);

  // Fetch paginated data for chart 1608 (Frequent Violators table) when Compliance view is selected
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Productivity')) {
      console.log('=== Productivity View Detected - Fetching Chart 1608 ===');
      fetchPaginatedData1608();
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchPaginatedData1608, selectedView]);

  // Fetch chart 1033 separately (Compliance Density Heatmap - Compliance view only)
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      fetchChartData(1033, setChartData1033);
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchChartData, selectedView]);

  // Fetch chart 1111 separately (India Heat Map - Compliance view only)
  useEffect(() => {
    if (user?.customer_id && selectedView.includes('Compliance')) {
      fetchChartData(1111, setChartData1111);
    }
  }, [user?.customer_id, filters.startDate, filters.endDate, fetchChartData, selectedView]);

  const fetchAllAnalyticsData = async () => {
    const viewKey = selectedView.toLowerCase().includes('safety')
      ? 'safety'
      : selectedView.toLowerCase().includes('compliance')
      ? 'compliance'
      : 'productivity';

    const chartConfigs = ANALYTICS_CONFIG[viewKey as keyof typeof ANALYTICS_CONFIG];

    try {
      // Extract chart IDs for the current view, excluding 1033 and 1111 (will be fetched separately)
      const chartIds = chartConfigs.map((item) => item.id).filter((id) => id !== 1033 && id !== 1111);

      // Prepare the request payload
      const payload: any = {
        chart_ids: chartIds,
      };

      // If vehicle IDs are selected, use them; otherwise use fleetId
      if (filters.vehicleIds && filters.vehicleIds.length > 0) {
        payload.vehicleIds = filters.vehicleIds;
      } else {
        payload.fleetId = getFleetIdForApi();
      }

      // If group IDs are selected, use them; otherwise use fleetId
      if (filters.vehicleGroup) {
        payload.vehicleGroup = filters.vehicleGroup;
      } else {
        payload.fleetId = getFleetIdForApi();
      }

      // Add dates only if they are provided in filters
      if (filters.startDate && filters.endDate) {
        payload.startdate = filters.startDate;
        payload.enddate = filters.endDate;
      } else {
        // Always include default 7-day range if no custom dates
        payload.startdate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        payload.enddate = dayjs().format('YYYY-MM-DD');
      }

      // Add fleetAdminId
      if (isRoleAdmin) payload.fleetAdminId = user.id;

      console.log('fetchAllAnalyticsData - Final Payload:', payload);

      const fleetId = getFleetIdForApi();
      const baseurl =
        fleetId == '685e1eec79b388464c5cd08e'
          ? 'https://backend.novusomnifleet.com'
          : 'https://novusai.novusomnifleet.com';

      const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/multi_chart_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the response data to match the existing structure
      const dataMap: { [key: string]: any } = {};

      // Map the response data to chart IDs
      Object.keys(data).forEach((chartId) => {
        const chartConfig = chartConfigs.find((config) => config.id.toString() === chartId);
        if (chartConfig) {
          const responseData = data[chartId];

          // Check if the response is an error object or valid data array
          if (responseData && typeof responseData === 'object' && responseData.error) {
            // Handle error response from API
            dataMap[chartId] = {
              chartId: parseInt(chartId),
              data: [], // Empty array for error cases
              config: chartConfig,
              error: true,
              errorMessage: responseData.error,
            };
          } else if (Array.isArray(responseData)) {
            // Handle successful data response
            dataMap[chartId] = {
              chartId: parseInt(chartId),
              data: responseData,
              config: chartConfig,
            };
          } else {
            // Handle unexpected response format
            dataMap[chartId] = {
              chartId: parseInt(chartId),
              data: [],
              config: chartConfig,
              error: true,
              errorMessage: 'Unexpected data format',
            };
          }
        }
      });

      setApiData(dataMap);
    } catch (error) {
      // Fallback: create empty data structure for error handling
      const dataMap: { [key: string]: any } = {};
      chartConfigs.forEach((item) => {
        dataMap[item.id] = {
          chartId: item.id,
          data: [],
          config: item,
          error: true,
        };
      });
      setApiData(dataMap);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (option?: string) => {
    if (option) {
      setSelectedView(option);
      setFilters((prev) => ({ ...prev, view: option }));
      // Store the selected view in sessionStorage
      sessionStorage.setItem('analyticsSelectedView', option);
      // Trigger custom event to notify TopNav
      window.dispatchEvent(new Event('analyticsViewChanged'));
    }
    setAnchorEl(null);
  };

  const handleOpenFilter = () => setIsFilterOpen(true);
  const handleCloseFilter = () => setIsFilterOpen(false);

  const handleApplyFilters = (newFilters: FilterState) => {
    console.log('🔍 handleApplyFilters - Received Filters:', newFilters);
    console.log('🔍 handleApplyFilters - Vehicle IDs:', newFilters.vehicleIds);
    console.log('🔍 handleApplyFilters - Vehicle IDs Length:', newFilters.vehicleIds?.length);

    if (newFilters.vehicleIds?.length === 0) {
      console.log('✅ FILTERS UPDATED - No vehicles selected, APIs will use fleetId');
    } else {
      console.log('✅ FILTERS UPDATED - Using selected vehicles:', newFilters.vehicleIds);
    }

    setFilters(newFilters);
    // Reset pagination when filters change
    setSafetyTablePaginationInfo({
      lastEvaluatedKey: null,
      hasMorePages: false,
      totalRecords: 0,
    });
    setTimeBucketTablePaginationInfo({
      lastEvaluatedKey: null,
      hasMorePages: false,
      totalRecords: 0,
    });
    // Show success toast message
    showToast('Filters applied successfully', 'success');
  };

  const handleResetFilters = () => {
    // Reset filters to default values
    const defaultFilters: FilterState = {
      vehicleIds: [],
      vehicleGroup: '',
      dateRange: '7 Days',
      view: selectedView,
    };

    setFilters(defaultFilters);
    // Reset pagination when filters are reset
    setSafetyTablePaginationInfo({
      lastEvaluatedKey: null,
      hasMorePages: false,
      totalRecords: 0,
    });
    setTimeBucketTablePaginationInfo({
      lastEvaluatedKey: null,
      hasMorePages: false,
      totalRecords: 0,
    });

    // Show success toast message
    showToast('Filters cleared successfully', 'success');
  };

  const handleCardDataUpdate = async (chartId: number, startDate: string, endDate: string) => {
    try {
      // Special handling for chart 1033 (Compliance Density Heatmap) - fetch from separate API
      if (chartId === 1033) {
        // Update chart-specific date range
        setChartDateRanges((prevRanges) => ({
          ...prevRanges,
          [chartId]: {
            startDate: startDate,
            endDate: endDate,
          },
        }));

        // Refetch chart 1033 from the separate endpoint with the new dates
        fetchChartData(1033, setChartData1033, startDate, endDate);
      }
      // Refetch chart 1111 from the separate endpoint with the new dates
      if (chartId === 1111) {
        // Update chart-specific date range
        setChartDateRanges((prevRanges) => ({
          ...prevRanges,
          [chartId]: {
            startDate: startDate,
            endDate: endDate,
          },
        }));

        // Refetch chart 1111 from the separate endpoint with the new dates
        fetchChartData(1111, setChartData1111, startDate, endDate);
        return;
      }

      // Determine which charts use paginate_by_chart_id vs multi_chart_data
      const chartIdsUsingPaginateByChartId = [1603, 1110, 1031, 1600, 1105, 1203, 1604, 1608];
      const usesPaginateByChartId = chartIdsUsingPaginateByChartId.includes(chartId);

      // Build payload with appropriate field names
      const payload: any = usesPaginateByChartId
        ? { chart_id: chartId, start_date: startDate, end_date: endDate }
        : { chart_ids: [chartId], startdate: startDate, enddate: endDate };

      // If vehicle IDs are selected, use them; otherwise use fleetId
      if (filters.vehicleIds && filters.vehicleIds.length > 0) {
        payload.vehicleIds = filters.vehicleIds;
      } else {
        payload.fleetId = getFleetIdForApi();
      }

      // If group IDs are selected, use them; otherwise use fleetId
      if (filters.vehicleGroup) {
        payload.vehicleGroup = filters.vehicleGroup;
      } else {
        payload.fleetId = getFleetIdForApi();
      }

      // Add fleetAdminId - ONLY when not filtering by specific vehicles
      if (!filters.vehicleIds || filters.vehicleIds.length === 0) {
        if (isRoleAdmin) payload.fleetAdminId = user.id;
      }

      console.log('handleCardDataUpdate - Final Payload for Chart', chartId, ':', payload);

      const fleetId = getFleetIdForApi();
      const baseurl =
        fleetId == '685e1eec79b388464c5cd08e'
          ? 'https://backend.novusomnifleet.com'
          : 'https://novusai.novusomnifleet.com';

      const apiEndpoint = usesPaginateByChartId ? 'paginate_by_chart_id' : 'multi_chart_data';

      const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/${apiEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Card data update response:', data);
      console.log('Chart ID:', chartId, 'Data for chart:', data[chartId]);

      // Update chart-specific date range (only for this chart, not global filters)
      setChartDateRanges((prevRanges) => ({
        ...prevRanges,
        [chartId]: {
          startDate: startDate,
          endDate: endDate,
        },
      }));

      // Update only the specific chart data
      setApiData((prevData) => {
        const chartConfig = getCurrentViewData().find((config) => config.id === chartId);
        if (chartConfig) {
          const responseData = data[chartId];

          // Handle null, undefined, or empty responses
          const isValidData = responseData && Array.isArray(responseData);

          return {
            ...prevData,
            [chartId]: {
              chartId: chartId,
              data: isValidData ? responseData : [],
              config: chartConfig,
              error: !isValidData,
            },
          };
        }
        return prevData;
      });

      // Special handling for heatmap chart (1005) - also refetch chartData1005
      if (chartId === 1005) {
        let url = `${baseurl}/api/v1/analytics_dashboard/api/chart_data/1005?start_date=${startDate}&end_date=${endDate}`;

        // If vehicle IDs are selected, use them; otherwise use fleet_manager_id
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          const vehicleIds = filters.vehicleIds.join(',');
          url += `&vehicle_ids=${vehicleIds}`;
        } else {
          url += `&fleet_manager_id=${getFleetIdForApi()}`;
        }

        // If group IDs are selected, use them; otherwise use fleet_manager_id
        if (filters.vehicleGroup) {
          const vehicleGroup = filters.vehicleGroup;
          url += `&vehicleGroup=${vehicleGroup}`;
        } else {
          url += `&fleet_manager_id=${getFleetIdForApi()}`;
        }

        // Add fleetAdminId parameter - use user.id for ROLE_ADMIN, user.parentId for others
        const fleetAdminId1005 = isRoleAdmin ? user.id : user.parentId;
        url += `&fleet_admin_id=${fleetAdminId1005}`;

        const heatmapResponse = await fetch(url, {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        });

        if (heatmapResponse.ok) {
          const heatmapData = await heatmapResponse.json();
          const dataArray = heatmapData.data || [];
          setChartData1005(dataArray);
        }
      }

      // Special handling for time bucket chart (1001) - also refetch chartData1001
      if (chartId === 1001) {
        let url = `${baseurl}/api/v1/analytics_dashboard/api/chart_data/1001?start_date=${startDate}&end_date=${endDate}`;

        // If vehicle IDs are selected, use them; otherwise use fleet_manager_id
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          const vehicleIds = filters.vehicleIds.join(',');
          url += `&vehicle_ids=${vehicleIds}`;
        } else {
          url += `&fleet_manager_id=${getFleetIdForApi()}`;
        }

        // If group IDs are selected, use them; otherwise use fleet_manager_id
        if (filters.vehicleGroup) {
          const vehicleGroup = filters.vehicleGroup;
          url += `&vehicleGroup=${vehicleGroup}`;
        } else {
          url += `&fleet_manager_id=${getFleetIdForApi()}`;
        }

        // Add fleetAdminId parameter - use user.id for ROLE_ADMIN, user.parentId for others
        const fleetAdminId1001 = isRoleAdmin ? user.id : user.parentId;
        url += `&fleet_admin_id=${fleetAdminId1001}`;

        const timeBucketResponse = await fetch(url, {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        });

        if (timeBucketResponse.ok) {
          const timeBucketData = await timeBucketResponse.json();
          const dataArray = timeBucketData.data || [];
          setChartData1001(dataArray);
        }
      }
    } catch (error) {}
  };

  const menuOptions = [
    { label: 'Safety (Default view)', value: 'Safety (Default view)' },
    { label: 'Compliance (Default view)', value: 'Compliance (Default view)' },
    { label: 'Productivity (Default view)', value: 'Productivity (Default view)' },
  ];

  // Get current view data
  const getCurrentViewData = () => {
    const viewKey = selectedView.toLowerCase().includes('safety')
      ? 'safety'
      : selectedView.toLowerCase().includes('compliance')
      ? 'compliance'
      : 'productivity';
    return ANALYTICS_CONFIG[viewKey as keyof typeof ANALYTICS_CONFIG];
  };

  const fetchNewPaginatedData = useCallback(
    async (cursor?: any) => {
      try {
        const startDate = filters.startDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        const endDate = filters.endDate || dayjs().format('YYYY-MM-DD');

        const payload: any = {
          chart_id: 1603,
          start_date: startDate,
          end_date: endDate,
        };

        // If vehicle IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleIds && filters.vehicleIds.length > 0) {
          payload.vehicle_ids = filters.vehicleIds;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // If group IDs are selected, use them; otherwise use fleetId
        if (filters.vehicleGroup) {
          payload.vehicleGroup = filters.vehicleGroup;
        } else {
          payload.fleetId = getFleetIdForApi();
        }

        // Add fleetAdminId
        if (isRoleAdmin) payload.fleetAdminId = user.id;

        // Only add next_cursor if it's provided (for next page)
        if (cursor) {
          payload.next_cursor = cursor;
        }

        const fleetId = getFleetIdForApi();

        const baseurl =
          fleetId == '685e1eec79b388464c5cd08e'
            ? 'https://backend.novusomnifleet.com'
            : 'https://novusai.novusomnifleet.com';

        const response = await fetch(`${baseurl}/api/v1/analytics_dashboard/api/paginate`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPaginatedData(data);
      } catch (error) {
        setPaginatedData(null);
      }
    },
    [user.customer_id, filters],
  );

  return (
    <Box className="min-h-screen text-white">
      <AnalyticsHeader
        selectedView={selectedView}
        anchorEl={anchorEl}
        open={open}
        menuOptions={menuOptions}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
        onOpenFilter={handleOpenFilter}
        onManageSectionsClick={handleManageSectionsClick}
        onManageViewsClick={handleManageSectionsClick2}
      />

      {selectedView === 'Safety (Default view)' && (
        <SafetyAnalyticsScreen
          apiData={apiData}
          chartConfigs={getCurrentViewData()}
          filters={filters}
          chartData1001={chartData1001}
          chartData1005={chartData1005}
          paginatedData={paginatedData}
          onPaginationChange={handlePaginationChange}
          onCardDataUpdate={handleCardDataUpdate}
          sectionsConfig={sectionsConfig}
          onFetchSafetyAnalytics={fetchSafetyDashboardAnalytics}
          safetyDashboardTableData={safetyDashboardTableData}
          timeBucketTableData={timeBucketTableData}
          safetyTablePaginationInfo={safetyTablePaginationInfo}
          timeBucketTablePaginationInfo={timeBucketTablePaginationInfo}
          heatmapTableData={heatmapTableData}
          heatmapTablePaginationInfo={heatmapTablePaginationInfo}
          onHeatmapTableIconClick={handleHeatmapTableIconClick}
          onUpdateHeatmapTableData={updateHeatmapTableData}
          onHeatmapTablePaginationChange={handleHeatmapTablePaginationChange}
          transformHeatmapDataToTable={transformHeatmapDataToTable}
          safetyAnalyticsCardData={safetyAnalyticsCardData}
          safetyAnalyticsCardPaginationInfo={safetyAnalyticsCardPaginationInfo}
          onFetchSingleTableData={fetchSingleTableData}
          onSafetyAnalyticsCardTablePagination={handleSafetyAnalyticsCardTablePagination}
        />
      )}
      {selectedView === 'Compliance (Default view)' && (
        <AnalyticsCompilanceScreen
          apiData={apiData}
          chartConfigs={getCurrentViewData()}
          filters={filters}
          chartDateRanges={chartDateRanges}
          chartData1033={chartData1033}
          chartData1111={chartData1111}
          sectionsConfig={sectionsConfig}
          complianceAnalyticsCardData={complianceAnalyticsCardData}
          complianceAnalyticsCardPaginationInfo={complianceAnalyticsCardPaginationInfo}
          onFetchSingleComplianceTableData={fetchSingleComplianceTableData}
          onComplianceAnalyticsCardTablePagination={handleComplianceAnalyticsCardTablePagination}
          paginatedData1039={paginatedData1039}
          onPaginationChange1039={handlePaginationChange1039}
          paginatedData1603={paginatedData1603}
          onPaginationChange1603={handlePaginationChange1603}
          paginatedData1110={paginatedData1110}
          onPaginationChange1110={handlePaginationChange1110}
          paginatedData1031={paginatedData1031}
          onPaginationChange1031={handlePaginationChange1031}
          paginatedData1600={paginatedData1600}
          onPaginationChange1600={handlePaginationChange1600}
          onCardDataUpdate={(chartId, startDate, endDate) => {
            if (chartId && startDate && endDate) {
              handleCardDataUpdate(chartId, startDate, endDate);
            }
          }}
        />
      )}
      {selectedView === 'Productivity (Default view)' && (
        <ProductivityAnalytics
          apiData={apiData}
          chartConfigs={getCurrentViewData()}
          filters={filters}
          sectionsConfig={sectionsConfig}
          onCardDataUpdate={handleCardDataUpdate}
          // Add these new props
          productivityAnalyticsCardData={productivityAnalyticsCardData}
          productivityAnalyticsCardPaginationInfo={productivityAnalyticsCardPaginationInfo}
          onFetchSingleTableData={fetchSingleProductivityTableData}
          onProductivityAnalyticsCardTablePagination={handleProductivityAnalyticsCardTablePagination}
          paginatedData1105={paginatedData1105}
          onPaginationChange1105={handlePaginationChange1105}
          paginatedData1203={paginatedData1203}
          onPaginationChange1203={handlePaginationChange1203}
          paginatedData1604={paginatedData1604}
          onPaginationChange1604={handlePaginationChange1604}
          paginatedData1608={paginatedData1608}
          onPaginationChange1608={handlePaginationChange1608}
        />
      )}

      <DashboardAnalyticsFilterData
        open={isFilterOpen}
        onClose={handleCloseFilter}
        selectedView={selectedView}
        vehicleOptions={vehicleOptions}
        groupOptions={groupOptions}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* ================= Manage Sections Dropdown ================= */}
      <Menu
        anchorEl={manageAnchorEl}
        open={openManageMenu}
        onClose={handleManageMenuClose}
        PaperProps={{
          sx: {
            minWidth: '232px',
            width: 'auto',
            opacity: 1,
            borderRadius: '8px',
            border: '1px solid #E0E0E0',
            backgroundColor: '#FFFFFF',
            boxShadow: '4px 4px 16px 0px rgba(30, 30, 30, 0.1)',
            marginTop: '4px',
          },
        }}
      >
        <MenuItem
          onClick={handleManageViewsClick}
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0%',
            color: '#1B1B1B',
          }}
        >
          <img
            src={ManageViewsIcon}
            alt="Manage Views"
            className="mr-2"
            style={{ width: '24px', height: '24px', opacity: 1 }}
          />
          Manage Views
        </MenuItem>
        <MenuItem
          onClick={handleManageViewsClick}
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0%',
            color: '#1B1B1B',
          }}
        >
          <img
            src={UpdateViewIcon}
            alt="Update Views"
            className="mr-2"
            style={{ width: '24px', height: '24px', opacity: 1 }}
          />
          Update View
        </MenuItem>
        <MenuItem
          onClick={handleDownloadClick}
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0%',
            color: '#1B1B1B',
          }}
        >
          <img
            src={DownloadIcon}
            alt="Download"
            className="mr-2"
            style={{ width: '24px', height: '24px', opacity: 1 }}
          />
          Download
        </MenuItem>
        <MenuItem
          onClick={handleDownloadClick}
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0%',
            color: '#C83333',
          }}
        >
          <img
            src={DeleteIcon}
            alt="Delete View"
            className="mr-2"
            style={{ width: '24px', height: '24px', opacity: 1 }}
          />
          Delete View
        </MenuItem>
      </Menu>

      <ManageViewModal open={isManageViewsModalOpen} onClose={handleCloseManageViewsModal} />

      {/* ================= Manage Sections Modal ================= */}
      <Modal open={isManageSectionsModalOpen} onClose={handleCloseManageSectionsModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '8px',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
            p: 2,
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
        >
          <AnalyticsManageSectionLayout
            onClose={handleCloseManageSectionsModal}
            sectionsConfig={sectionsConfig}
            setSectionsConfig={setSectionsConfig}
            onToggleSection={handleToggleSection}
            currentView={selectedView}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default DashboardAnalyticScreen;
